"use client";

import { Swap } from "@/components/icons/Swap";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { WidgetParams } from "@/components/widgets";
import { WidgetContent } from "@/components/widgets/SideDrawerWidgetContent";
import { useWidgetAnalytics } from "@/context/AnalyticsProvider";
import { type WidgetTab, useRightNav } from "@/context/RightNavContext";
import * as checkout from "@imtbl/checkout-sdk";
import { ArrowUpDown, CreditCard } from "lucide-react";
import { useCallback, useMemo } from "react";

export type FundTab = "swap" | "bridge" | "buy";

export function FundWidgetContent() {
  const { trackWidgetOpen } = useWidgetAnalytics();
  const { openWidgetTab, widgetConfig } = useRightNav();

  const handleTabChange = useCallback(
    (value: string) => {
      const tab = value as WidgetTab;
      trackWidgetOpen({
        widgetTab: tab,
        id: `fund-${value.toLowerCase()}-tab`,
        type: "tab",
        label: value,
        section: "FundWidget",
      });
      openWidgetTab({ tab, from: "RightNav" });
    },
    [trackWidgetOpen, openWidgetTab],
  );

  const widgetParams = useMemo((): WidgetParams | null => {
    if (!widgetConfig) return null;

    switch (widgetConfig.tab) {
      case "Swap":
        return {
          type: "CommerceWidgetFlow",
          params: {
            flow: checkout.CommerceFlowType.SWAP,
            ...(widgetConfig.targetTokenAddress && {
              toTokenAddress: widgetConfig.targetTokenAddress,
            }),
          },
          config: {
            SWAP: {
              showHeader: false,
              customSubTitle: "",
            },
            themeOverrides: {
              drawerBackground: "transparent",
            },
          },
        };
      case "Bridge":
        return { type: "LayerSwap" };
      case "Buy":
        return {
          type: "CommerceWidgetFlow",
          params: {
            flow: checkout.CommerceFlowType.ONRAMP,
            ...(widgetConfig.targetTokenAddress && {
              tokenAddress: widgetConfig.targetTokenAddress,
            }),
          },
          config: {
            ONRAMP: {
              showHeader: false,
              showMenu: false,
              customSubTitle: "",
            },
          },
        };
      default:
        return null;
    }
  }, [widgetConfig]);

  if (!widgetConfig) return null;

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 pt-4 pb-3">
        <Tabs value={widgetConfig.tab} onValueChange={handleTabChange}>
          <TabsList variant="glass" className="grid w-full grid-cols-3 gap-2">
            <TabsTrigger value="Swap" className="gap-2">
              <Swap sx={{ fontSize: "16px" }} />
              <span>Swap</span>
            </TabsTrigger>
            <TabsTrigger value="Bridge" className="gap-2">
              <ArrowUpDown className="w-4 h-4" />
              <span>Bridge</span>
            </TabsTrigger>
            <TabsTrigger value="Buy" className="gap-2">
              <CreditCard className="w-4 h-4" />
              <span>Buy</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div
        className="flex-1 min-h-0 w-full max-w-full overflow-hidden
        [&_[id^='checkout-container-']]:!w-full
        [&_[id^='checkout-container-']]:!h-full
        [&_[id^='checkout-container-']]:!max-h-full
        [&_[id^='checkout-container-']]:!overflow-auto
        [&_[data-testid='simpleLayout']]:!w-full
        [&_[data-testid='simpleLayout']]:!h-full
        [&_[data-testid='simpleLayout']]:!max-h-full
        [&_[data-testid='simpleLayout']]:!overflow-auto
        [&_[data-testid='container']]:!bg-transparent
        [&_[data-testid='container']]:!h-full
        [&_[data-testid='container']]:!max-h-full
        [&_[data-testid='container']]:!overflow-auto
        [&_[data-testid='header-navigation-container']]:!bg-transparent
        [&_[data-testid='header-navigation-container']]:!backdrop-blur-none
        [&_[data-testid='service-unavailable-to-region-error-view']]:!overflow-hidden"
      >
        <WidgetContent widgetParams={widgetParams} />
      </div>
    </div>
  );
}
